import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SearchBoxProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SearchBox({ isOpen, onClose }: SearchBoxProps) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        searchBoxRef.current &&
        !searchBoxRef.current.contains(event.target as Node) &&
        isOpen
      ) {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      onClose();
      setQuery('');
    }
  };

  return (
    <div
      ref={searchBoxRef}
      className={cn(
        'absolute right-0 top-1/2 -translate-y-1/2 transform transition-all duration-200 ease-in-out z-50',
        isOpen ? 'w-[350px] opacity-100' : 'w-0 opacity-0 pointer-events-none'
      )}
    >
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg">
          <div className="absolute left-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </div>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search..."
            className={cn(
              'w-full pl-10 pr-10 py-2 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-blue-500',
              'text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400',
              'rounded-lg'
            )}
            style={{ paddingLeft: '2.5rem' }}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </form>
    </div>
  );
}